import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@/app/services/features';
import { toast } from '@/components/ui/toast';
import { ServerError } from '@/services/bulk-donor-schedule';
import { ProgressStreamError } from '@/services/progress-stream';

function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof ServerError) {
    return error.detail;
  }
  if (error instanceof ProgressStreamError) {
    const message = (error as ProgressStreamError<{ message: string }>).error
      ?.message;
    return typeof message === 'string' && message.trim() ? message : undefined;
  }
}

export function useNotifications() {
  const { t } = useTranslation();
  const showErrorDetails = useFeatureFlag('bulk_ds_handle_duplicate_breeds');

  return useMemo(
    () => ({
      validationErrors() {
        toast.add({
          data: { variant: 'error' },
          title: t('bulkDonorScheduling.toast.Validation_Errors.summary'),
          description: t('bulkDonorScheduling.toast.Validation_Errors.detail'),
        });
      },
      validationSuccessful() {
        toast.add({
          data: { variant: 'success' },
          title: t('bulkDonorScheduling.toast.Validation_Successful.summary'),
          description: t(
            'bulkDonorScheduling.toast.Validation_Successful.detail',
          ),
        });
      },
      validationServerError(error?: unknown) {
        const fallback = t(
          'bulkDonorScheduling.toast.Validation_Server_Error.detail',
        );
        toast.add({
          data: { variant: 'error' },
          title: t('bulkDonorScheduling.toast.Validation_Server_Error.summary'),
          description: showErrorDetails
            ? (getErrorMessage(error) ?? fallback)
            : fallback,
        });
      },
      uploadSuccessful() {
        toast.add({
          data: { variant: 'success' },
          title: t('bulkDonorScheduling.toast.Upload_Successful.summary'),
          description: t('bulkDonorScheduling.toast.Upload_Successful.detail'),
        });
      },
      processingSuccessful(result: {
        donorSchedulesProcessed: number;
        soSchedulesCreated: number;
        chuteSchedulesCreated: number;
        successfulRows: number;
        manualFollowUpRows: number;
      }) {
        toast.add({
          data: { variant: 'success' },
          title: t('bulkDonorScheduling.toast.Processing_Successful.summary'),
          description: t(
            'bulkDonorScheduling.toast.Processing_Successful.detail',
            {
              donor_schedules_processed: result.donorSchedulesProcessed,
              so_schedules_created: result.soSchedulesCreated,
              chute_schedules_created: result.chuteSchedulesCreated,
              successful_rows: result.successfulRows,
              manual_follow_up_rows:
                result.manualFollowUpRows ||
                t('bulkDonorScheduling.toast.Processing_Successful.none'),
            },
          ),
        });
      },
      uploadServerError(error?: unknown) {
        const fallback = t(
          'bulkDonorScheduling.toast.Upload_Server_Error.detail',
        );
        toast.add({
          data: { variant: 'error' },
          title: t('bulkDonorScheduling.toast.Upload_Server_Error.summary'),
          description: showErrorDetails
            ? (getErrorMessage(error) ?? fallback)
            : fallback,
        });
      },
      fileProcessingError(message: string) {
        toast.add({
          data: { variant: 'error' },
          title: t('bulkDonorScheduling.fileProcessingError'),
          description: message,
        });
      },
      fileUploadSuccess(message: string) {
        toast.add({
          data: { variant: 'success' },
          title: t('bulkDonorScheduling.fileUploadSuccess'),
          description: message,
        });
      },
      prioritiesCalculated(result: {
        schedulesUpdated: number;
        schedulesCleared: number;
        excluded: { donorName: string; reason: string }[];
        priorities: { reason?: string | null; psiMismatch?: boolean }[];
      }) {
        const donorList = (
          entries: { donorName: string; reason: string }[],
        ): string => {
          const names = entries
            .map((entry) => entry.donorName)
            .filter((name) => name.length > 0);
          if (names.length <= 5) {
            return names.join(', ');
          }
          return t('bulkDonorScheduling.toast.Priorities_Calculated.andMore', {
            donors: names.slice(0, 5).join(', '),
            remaining: names.length - 5,
          });
        };

        const missingPsi = result.excluded.filter(
          (entry) => entry.reason === 'MISSING_PSI',
        );
        const duplicates = result.excluded.filter(
          (entry) => entry.reason === 'DUPLICATE_DONOR_DATE',
        );
        const psiMismatches = result.priorities.filter(
          (priority) => priority.psiMismatch,
        ).length;
        const notScheduledYet = result.priorities.filter(
          (priority) => priority.reason === 'NO_SCHEDULE_ON_DATE',
        ).length;
        const ambiguous = result.priorities.filter(
          (priority) => priority.reason === 'AMBIGUOUS_DONOR_NAME',
        ).length;

        const lines = [
          result.schedulesUpdated > 0
            ? t('bulkDonorScheduling.toast.Priorities_Calculated.updated', {
                schedules_updated: result.schedulesUpdated,
              })
            : t('bulkDonorScheduling.toast.Priorities_Calculated.detail'),
        ];
        if (missingPsi.length > 0) {
          lines.push(
            t('bulkDonorScheduling.toast.Priorities_Calculated.missingPsi', {
              count: missingPsi.length,
              donors: donorList(missingPsi),
            }),
          );
        }
        if (duplicates.length > 0) {
          lines.push(
            t('bulkDonorScheduling.toast.Priorities_Calculated.duplicates', {
              count: duplicates.length,
              donors: donorList(duplicates),
            }),
          );
        }
        if (psiMismatches > 0) {
          lines.push(
            t('bulkDonorScheduling.toast.Priorities_Calculated.psiMismatch', {
              count: psiMismatches,
            }),
          );
        }
        if (notScheduledYet > 0) {
          lines.push(
            t('bulkDonorScheduling.toast.Priorities_Calculated.notScheduled', {
              count: notScheduledYet,
            }),
          );
        }
        if (ambiguous > 0) {
          lines.push(
            t('bulkDonorScheduling.toast.Priorities_Calculated.ambiguous', {
              count: ambiguous,
            }),
          );
        }

        toast.add({
          data: { variant: lines.length > 1 ? 'warning' : 'success' },
          title: t('bulkDonorScheduling.toast.Priorities_Calculated.summary'),
          description: lines.join(' '),
        });
      },
    }),
    [showErrorDetails, t],
  );
}
